import { useEffect } from "react";
import type { RefObject } from "react";
import { track } from "../../lib/events";
import type { ContentItem } from "../../types";

const YOUTUBE_STATES: Record<number, string> = {
  0: "ended",
  1: "playing",
  2: "paused",
  3: "buffering",
};

function parse(data: unknown): Record<string, unknown> | null {
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return data && typeof data === "object" ? (data as Record<string, unknown>) : null;
}

// YouTube and Vimeo players tell the page around them what the viewer does, if
// the page asks. Those messages become embed_state events (playing, paused,
// ended, with the position). Other providers (Google Slides, Forms, Drive)
// give the page nothing, so for them only opening and leaving are recorded.
export function useEmbedEvents(
  frame: RefObject<HTMLIFrameElement | null>,
  item: ContentItem,
  moduleID: string,
  provider: string | undefined,
  enabled: boolean
) {
  useEffect(() => {
    const element = frame.current;
    if (!enabled || !element || (provider !== "youtube" && provider !== "vimeo")) return;
    const origin = provider === "youtube" ? "https://www.youtube.com" : "https://player.vimeo.com";
    let position = 0;
    let lastState = "";
    const emit = (state: string) => {
      if (state === lastState) return;
      lastState = state;
      track("embed_state", {
        module_id: moduleID,
        item_id: item.id,
        payload: { provider, state, position_seconds: Math.round(position * 10) / 10 },
      });
    };
    const send = (message: unknown) =>
      element.contentWindow?.postMessage(JSON.stringify(message), origin);
    const subscribe = () => {
      if (provider === "youtube") {
        send({ event: "listening", id: 1, channel: "widget" });
        send({ event: "command", func: "addEventListener", args: ["onStateChange"], id: 1 });
      } else {
        ["play", "pause", "finish", "timeupdate"].forEach((name) =>
          send({ method: "addEventListener", value: name })
        );
      }
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== element.contentWindow) return;
      const message = parse(event.data);
      if (!message) return;
      if (provider === "youtube") {
        const info = message.info as Record<string, unknown> | undefined;
        if (typeof info?.currentTime === "number") position = info.currentTime;
        if (message.event === "onStateChange" && typeof message.info === "number") {
          const state = YOUTUBE_STATES[message.info];
          if (state) emit(state);
        } else if (message.event === "infoDelivery" && typeof info?.playerState === "number") {
          const state = YOUTUBE_STATES[info.playerState];
          if (state) emit(state);
        }
      } else {
        const data = message.data as Record<string, unknown> | undefined;
        if (typeof data?.seconds === "number") position = data.seconds;
        const states: Record<string, string> = {
          play: "playing",
          pause: "paused",
          finish: "ended",
        };
        if (typeof message.event === "string" && states[message.event]) emit(states[message.event]);
      }
    };
    window.addEventListener("message", onMessage);
    element.addEventListener("load", subscribe);
    subscribe();
    return () => {
      window.removeEventListener("message", onMessage);
      element.removeEventListener("load", subscribe);
    };
  }, [frame, item.id, moduleID, provider, enabled]);
}
